"use client"

import { useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/stores/auth-store"
import { useRemindersStore } from "@/stores/reminders-store"
import { usePopupStackStore } from "@/stores/popup-stack-store"
import { LeadNotificationCard, type LeadNotificationDetails } from "@/components/lead-notification-card"
import { useToastNotifier } from "@/hooks/use-toast-notifier"
import { getReminders, markReminderRead } from "@/lib/reminders-api"
import api from "@/lib/api"

const POLL_INTERVAL_MS = 10_000
// Store actions are stable, so the polling effect can call them directly.
const { track, untrack } = usePopupStackStore.getState()
const UNNOTIFIED_ENDPOINT = "/leads/admin-unnotified?source=meta_ads_zapier&limit=10"

type UnnotifiedLead = {
  id: number | string
  full_name: string | null
  campaign_name: string | null
  phone?: string | null
  created_at?: string | null
}

// The bell is the per-user source for all enabled lead intake paths. Keep the
// older Zapier feed for clinics that have not enabled new-lead bell alerts.
export function MetaLeadNotifier() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.id)
  // Admin-only: the "New lead:" bells and the legacy Zapier feed are a desk
  // workflow. Assignment popups below go to whichever user is the assignee.
  const isAdmin = useAuthStore((s) => s.user?.roles?.includes("admin") ?? false)
  const zapierEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.meta_ads_zapier_intake_enabled ?? false
  )
  // Smile 260917: the assignee gets the same sticky popup + chime when a
  // lead is assigned to them. Rides the LeadAssignmentNotifier bell rows
  // ("New lead assigned: …", is_system, user_id = assignee), so it is gated
  // by the same flag that creates them.
  const assignmentEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.lead_assignment_notifications_enabled ?? false
  )
  // Duplicate flag mode (Radiance 260921): the red "Duplicate lead" card.
  const duplicatesEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.duplicate_leads_flag_mode ?? false
  )
  // Radiance 261021: the consultant's popup when someone else writes a note
  // on their lead. Rides the LeadNoteNotifier bell rows ("New note on: …",
  // is_system, user_id = consultant), gated by the flag that creates them.
  const notesEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.lead_note_notifications_enabled ?? false
  )
  const router = useRouter()
  const notify = useToastNotifier()
  const t = useTranslations("notifications")
  const deliveredRef = useRef({ userId: "", leads: new Set<string>() })
  // Radiance 260921: on a busy morning the stack piles up ten cards that had
  // to be closed one by one. Every live popup registers in the shared popup
  // stack so a single button can clear the lot (appointment reminders too).
  const activeCount = usePopupStackStore((s) => s.count)
  const dismissAll = usePopupStackStore((s) => s.dismissAll)

  useEffect(() => {
    if (!isAuthenticated || userId == null || (!isAdmin && !assignmentEnabled && !duplicatesEnabled && !notesEnabled)) {
      deliveredRef.current = { userId: "", leads: new Set() }
      return
    }

    const owner = String(userId)
    if (deliveredRef.current.userId !== owner) {
      deliveredRef.current = { userId: owner, leads: new Set() }
    }
    const delivered = deliveredRef.current.leads
    const storageKey = `lead-popups.delivered.v1:${owner}`
    let cancelled = false
    let polling = false
    const toastIds = new Set<string | number>()

    const readDelivered = () => {
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]")
        if (Array.isArray(saved)) {
          saved.forEach((id: unknown) => {
            if (typeof id === "string") delivered.add(id)
          })
        }
      } catch { /* In-memory dedup still works when browser storage is unavailable. */ }
    }

    // `popupKey` is the dedup key: new-lead popups key on the lead id (one
    // popup per lead), assignment popups on the bell row so a later
    // re-assignment of the same lead pops again.
    const showLead = (id: string, title: string, description: string, reminderId?: string, details: LeadNotificationDetails = {}, popupKey: string = id, tone: "default" | "danger" = "default") => {
      if (cancelled || delivered.has(popupKey)) return
      const acknowledge = () => {
        if (!reminderId) return
        void markReminderRead(reminderId)
          .then(() => useRemindersStore.getState().refreshAll())
          .catch(() => { /* Leave the bell unread if acknowledgement fails. */ })
      }
      const toastId = notify({
        title,
        description,
        actionLabel: t("metaLeadNotifier.viewLead"),
        onAction: () => {
          acknowledge()
          router.push(`/leads/${id}`)
        },
        onDismiss: acknowledge,
        render: (popupId) => (
          <LeadNotificationCard
            name={description}
            {...details}
            tone={tone}
            onOpen={() => {
              acknowledge()
              untrack(popupId)
              toast.dismiss(popupId)
              router.push(`/leads/${id}`)
            }}
            onDismiss={() => {
              acknowledge()
              untrack(popupId)
              toast.dismiss(popupId)
            }}
          />
        ),
      })
      toastIds.add(toastId)
      track(toastId, acknowledge)
      delivered.add(popupKey)
      try {
        localStorage.setItem(storageKey, JSON.stringify([...delivered]))
      } catch { /* Keep the popup usable even with blocked/full browser storage. */ }
    }

    const pollBells = async () => {
      if (!isAdmin) return
      let page = 1
      let lastPage = 1
      do {
        const response = await getReminders({
          search: "New lead:", status: "unread", timing: "due",
          sort: "created_at", direction: "desc", per_page: 100, page,
        })
        if (cancelled) return
        for (const reminder of response.data) {
          if (!reminder.is_system || !reminder.lead_id ||
              String(reminder.user_id) !== owner || !reminder.title.startsWith("New lead:")) continue
          showLead(
            reminder.lead_id,
            t("metaLeadNotifier.fallbackName"),
            reminder.lead?.full_name?.trim() || t("metaLeadNotifier.fallbackName"),
            reminder.id,
            {
              phone: reminder.lead?.phone,
              source: reminder.lead?.source_name,
              campaign: reminder.lead?.campaign_name,
              receivedAt: reminder.created_at,
            },
          )
        }
        lastPage = response.meta?.last_page ?? 1
        page++
      } while (!cancelled && page <= lastPage)
    }

    const pollAssignments = async () => {
      if (!assignmentEnabled || cancelled) return
      let page = 1
      let lastPage = 1
      do {
        const response = await getReminders({
          search: "New lead assigned:", status: "unread", timing: "due",
          sort: "created_at", direction: "desc", per_page: 100, page,
        })
        if (cancelled) return
        for (const reminder of response.data) {
          if (!reminder.is_system || !reminder.lead_id ||
              String(reminder.user_id) !== owner || !reminder.title.startsWith("New lead assigned:")) continue
          showLead(
            reminder.lead_id,
            t("metaLeadNotifier.assignedTitle"),
            reminder.lead?.full_name?.trim() || t("metaLeadNotifier.fallbackName"),
            reminder.id,
            {
              heading: t("metaLeadNotifier.assignedTitle"),
              phone: reminder.lead?.phone,
              source: reminder.description,
              receivedAt: reminder.created_at,
            },
            `assigned:${reminder.id}`,
          )
        }
        lastPage = response.meta?.last_page ?? 1
        page++
      } while (!cancelled && page <= lastPage)
    }

    // Radiance 260921: duplicate flag mode rings "Duplicate lead:" to the
    // desk roles AND the original lead's consultant, so this poll is not
    // admin-gated — whoever holds the bell row sees the red card.
    const pollDuplicates = async () => {
      if (!duplicatesEnabled || cancelled) return
      let page = 1
      let lastPage = 1
      do {
        const response = await getReminders({
          search: "Duplicate lead:", status: "unread", timing: "due",
          sort: "created_at", direction: "desc", per_page: 100, page,
        })
        if (cancelled) return
        for (const reminder of response.data) {
          if (!reminder.is_system || !reminder.lead_id ||
              String(reminder.user_id) !== owner || !reminder.title.startsWith("Duplicate lead:")) continue
          showLead(
            reminder.lead_id,
            t("metaLeadNotifier.duplicateTitle"),
            reminder.lead?.full_name?.trim() || t("metaLeadNotifier.fallbackName"),
            reminder.id,
            {
              heading: t("metaLeadNotifier.duplicateTitle"),
              phone: reminder.lead?.phone,
              source: reminder.description,
              campaign: reminder.lead?.campaign_name,
              receivedAt: reminder.created_at,
            },
            `duplicate:${reminder.id}`,
            "danger",
          )
        }
        lastPage = response.meta?.last_page ?? 1
        page++
      } while (!cancelled && page <= lastPage)
    }

    const pollNotes = async () => {
      if (!notesEnabled || cancelled) return
      let page = 1
      let lastPage = 1
      do {
        const response = await getReminders({
          search: "New note on:", status: "unread", timing: "due",
          sort: "created_at", direction: "desc", per_page: 100, page,
        })
        if (cancelled) return
        for (const reminder of response.data) {
          if (!reminder.is_system || !reminder.lead_id ||
              String(reminder.user_id) !== owner || !reminder.title.startsWith("New note on:")) continue
          showLead(
            reminder.lead_id,
            t("metaLeadNotifier.noteTitle"),
            reminder.lead?.full_name?.trim() || t("metaLeadNotifier.fallbackName"),
            reminder.id,
            {
              heading: t("metaLeadNotifier.noteTitle"),
              phone: reminder.lead?.phone,
              // "{author}: {excerpt}" — the description is the note itself.
              source: reminder.description,
              receivedAt: reminder.created_at,
            },
            `note:${reminder.id}`,
          )
        }
        lastPage = response.meta?.last_page ?? 1
        page++
      } while (!cancelled && page <= lastPage)
    }

    const pollZapier = async () => {
      if (!zapierEnabled || cancelled) return
      const response = await api.get<{ data: UnnotifiedLead[] }>(UNNOTIFIED_ENDPOINT)
      if (cancelled) return
      for (const lead of response.data.data ?? []) {
        const name = lead.full_name?.trim() || t("metaLeadNotifier.fallbackName")
        showLead(String(lead.id), t("metaLeadNotifier.title"),
          name, undefined, {
            phone: lead.phone, source: "Meta Ads", campaign: lead.campaign_name, receivedAt: lead.created_at,
          })
        // Retry acknowledgements even when the popup was already delivered.
        void api.post(`/leads/${lead.id}/mark-admin-notified`).catch(() => {})
      }
    }

    const pollOnce = async () => {
      if (cancelled || polling) return
      polling = true
      readDelivered()
      try {
        await pollBells().catch(() => {})
        await pollAssignments().catch(() => {})
        await pollDuplicates().catch(() => {})
        await pollNotes().catch(() => {})
        await pollZapier().catch(() => {})
      } finally {
        polling = false
      }
    }

    void pollOnce()
    const interval = setInterval(pollOnce, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
      toastIds.forEach((id) => {
        toast.dismiss(id)
        untrack(id)
      })
    }
  }, [isAuthenticated, isAdmin, userId, zapierEnabled, assignmentEnabled, duplicatesEnabled, notesEnabled, router, notify, t])

  // One card has its own X; the button only earns its place from two up.
  //
  // Radiance 260923: the first version sat in the 32px strip ABOVE the stack
  // at z-[10000] — but sonner's toaster is z-index 999999999 and its cards
  // carry an invisible hover bridge over that strip, so the pill drew behind
  // the cards and most clicks landed on them. It now sits BESIDE the stack,
  // level with the front card (sonner: 24px offset, 356px wide), on a layer
  // above the toaster. Below 600px sonner goes full-width, so there it drops
  // to the bottom-centre instead of fighting the cards for the same pixels.
  if (activeCount < 2) return null

  return (
    <div className="pointer-events-none fixed print:hidden right-[calc(24px+356px+12px)] top-[24px] z-[1000000000] max-[600px]:inset-x-0 max-[600px]:bottom-4 max-[600px]:top-auto max-[600px]:right-auto max-[600px]:flex max-[600px]:justify-center">
      <Button
        type="button"
        variant="default"
        size="sm"
        className="pointer-events-auto h-9 gap-1.5 px-3.5 text-[13px] font-medium shadow-lg"
        onClick={dismissAll}
      >
        <X className="size-3.5" aria-hidden="true" />
        {t("leadPopup.dismissAll", { count: activeCount })}
      </Button>
    </div>
  )
}
